// src/pages/accounting/budget/BudgetYearlyNew.jsx
import React, { useState, useRef } from "react";

import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../../../auth";
import styles from "./BudgetYearlyNew.module.css";

const API_DOC = "/api/budget/documents/";


const BUSINESS_UNIT_OPTIONS = [
  { value: "company",          label: "전사" },
  { value: "management",       label: "경영지원" },
  { value: "service",          label: "용역사업" },
  { value: "env_total",        label: "환경위생(종합)" },
  { value: "env_disinfection", label: "환경위생(소독)" },
  { value: "cafe_gasan",       label: "카페(가산)" },
  { value: "cafe_doksan",      label: "카페(독산)" },
  { value: "cafe_geumnarae",   label: "카페(금나래)" },
];


export default function BudgetYearlyNew() {
  const navigate = useNavigate();
  const fileRef  = useRef(null);

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear + 1; y >= currentYear - 3; y--) yearOptions.push(y);

  // ── 폼 상태 ──────────────────────────────────
  const [form, setForm] = useState({
    title        : "",
    fiscal_year  : String(currentYear),
    business_unit: "company",
    description  : "",
  });
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState("");

  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    // 파일명으로 제목 자동 채우기 (확장자 제거)
    if (!form.title) {
      setForm(prev => ({ ...prev, title: f.name.replace(/\.(xlsx?|xlsm)$/i, "") }));
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    setFile(f);
    if (!form.title) {
      setForm(prev => ({ ...prev, title: f.name.replace(/\.(xlsx?|xlsm)$/i, "") }));
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { setError("예산서명을 입력하세요."); return; }
    if (!file)              { setError("xlsx 파일을 선택하세요."); return; }
    setError("");
    setUploading(true);

    try {
      const fd = new FormData();
      fd.append("title",         form.title.trim());
      fd.append("fiscal_year",   form.fiscal_year);
      fd.append("business_unit", form.business_unit);
      fd.append("description",   form.description);
      fd.append("original_file", file);

      const res = await fetch(API_DOC, {
        method : "POST",
        headers: { Authorization: `Bearer ${getAccessToken()}` },
        body   : fd,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || JSON.stringify(err) || `서버 오류 (${res.status})`);
      }

      const doc = await res.json();
      // 업로드 성공 → 상세(뷰어) 페이지로 이동
      navigate(`/accounting/budget/yearly/${doc.id}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        {/* 헤더 */}
        <div className={styles.cardHeader}>
          <h2 className={styles.title}>연도별 예산서 업로드</h2>
          <button
            className={styles.btnBack}
            onClick={() => navigate("/accounting/budget/yearly/list")}
          >
            ← 목록으로
          </button>
        </div>

        {/* 에러 */}
        {error && <p className={styles.errorMsg}>{error}</p>}

        {/* 폼 */}
        <div className={styles.formGrid}>
          {/* 예산서명 */}
          <div className={styles.fieldFull}>
            <label className={styles.label}>예산서명 *</label>
            <input
              name="title"
              className={styles.input}
              placeholder="예) 2025년 전사 예산서"
              value={form.title}
              onChange={handleChange}
            />
          </div>

          {/* 회계연도 */}
          <div className={styles.field}>
            <label className={styles.label}>회계연도 *</label>
            <select
              name="fiscal_year"
              className={styles.select}
              value={form.fiscal_year}
              onChange={handleChange}
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}년</option>
              ))}
            </select>
          </div>

          {/* 사업부 */}
          <div className={styles.field}>
            <label className={styles.label}>사업부 *</label>
            <select
              name="business_unit"
              className={styles.select}
              value={form.business_unit}
              onChange={handleChange}
            >
              {BUSINESS_UNIT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* 설명 */}
          <div className={styles.fieldFull}>
            <label className={styles.label}>설명 (선택)</label>
            <textarea
              name="description"
              className={styles.textarea}
              rows={2}
              placeholder="예) 이사회 제출용 최종본"
              value={form.description}
              onChange={handleChange}
            />
          </div>

          {/* 파일 업로드 드래그앤드롭 */}
          <div className={styles.fieldFull}>
            <label className={styles.label}>엑셀 파일 (.xlsx / .xlsm) *</label>
            <div
              className={`${styles.dropZone} ${file ? styles.dropZoneActive : ""}`}
              onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
            >
              {file ? (
                <div className={styles.fileInfo}>
                  <span className={styles.fileIcon}>📊</span>
                  <span className={styles.fileName}>{file.name}</span>
                  <span className={styles.fileSize}>
                    ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                  <button
                    className={styles.btnClear}
                    onClick={e => { e.stopPropagation(); setFile(null); }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className={styles.dropHint}>
                  <span className={styles.dropIcon}>📂</span>
                  <p>여기에 파일을 드래그하거나 클릭하여 선택하세요</p>
                  <p className={styles.dropSub}>.xlsx / .xlsm, 최대 20MB</p>
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
        </div>

        {/* 제출 버튼 */}
        <div className={styles.btnRow}>
          <button
            className={styles.btnCancel}
            onClick={() => navigate("/accounting/budget/yearly/list")}
            disabled={uploading}
          >
            취소
          </button>
          <button
            className={styles.btnSubmit}
            onClick={handleSubmit}
            disabled={uploading}
          >
            {uploading ? "업로드 중…" : "업로드 및 저장"}
          </button>
        </div>
      </div>
    </div>
  );
}